import { FC, useState } from 'react';
import {
  Stack,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from '@mui/material';
import { SelfServeDataAccessStatus } from '@constants/constants';

const isArchiveButtonVisible = (
  status: SelfServeDataAccessStatus | null,
  isArchived: boolean,
): boolean => {
  // If request is archived, always show unarchive button
  if (isArchived) return true;

  if (!status) return false;

  const allowedStatuses = [
    SelfServeDataAccessStatus.PENDING_USER_CREATION,
    SelfServeDataAccessStatus.PENDING_NDA_CREATION,
    SelfServeDataAccessStatus.READY_TO_SEND_NDA,
    SelfServeDataAccessStatus.PENDING_NDA_SIGNATURE,
    SelfServeDataAccessStatus.PENDING_ACCESS,
    SelfServeDataAccessStatus.CALL_SCHEDULED,
  ];

  return allowedStatuses.includes(status);
};

interface DeleteArchiveSectionProps {
  isArchived: boolean;
  isDeleting: boolean;
  isTogglingArchive: boolean;
  onDelete: () => void;
  onArchive: () => void;
  requestStatus: SelfServeDataAccessStatus | null;
}

const DeleteArchiveSection: FC<DeleteArchiveSectionProps> = ({
  isArchived,
  isDeleting,
  isTogglingArchive,
  onDelete,
  onArchive,
  requestStatus,
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const theme = useTheme();

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    setIsDeleteDialogOpen(false);
    onDelete();
  };

  return (
    <>
      <Stack
        gap={'1rem'}
        sx={{
          background: '#fff',
          borderRadius: '1rem',
          p: '1rem',
        }}
      >
        <Typography fontSize={'16px'} lineHeight={'22px'} fontWeight={700} color={'#6C6C6C'}>
          Delete/ Archive Request
        </Typography>

        <Stack direction={'row'} justifyContent={'space-between'}>
          <Button
            variant="outlined"
            disableElevation
            disableFocusRipple
            disableRipple
            disableTouchRipple
            disabled={isDeleting}
            onClick={handleDeleteClick}
            sx={{
              textTransform: 'none',
              borderRadius: '32px',
              color: '#FF6347',
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              padding: '10px 24px',
              border: '1px solid #2E4A4F',
              background: '#fff',
              '&:hover': {
                border: '1px solid #2E4A4F',
                background: '#fff',
              },
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Request'}
          </Button>

          {isArchiveButtonVisible(requestStatus, isArchived) && (
            <Button
              variant={isArchived ? 'contained' : 'outlined'}
              disableElevation
              disableFocusRipple
              disableRipple
              disableTouchRipple
              disabled={isTogglingArchive}
              onClick={onArchive}
              sx={{
                textTransform: 'none',
                borderRadius: '32px',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px',
                padding: '10px 24px',
                ...(isArchived
                  ? {
                      color: '#fff',
                      background: '#2F736E',
                      border: '1px solid #2F736E',
                      '&:hover': {
                        background: '#2F736E',
                        border: '1px solid #2F736E',
                      },
                    }
                  : {
                      color: '#6C6C6C',
                      border: '1px solid #2E4A4F',
                      background: '#fff',
                      '&:hover': {
                        border: '1px solid #2E4A4F',
                        background: '#fff',
                      },
                    }),
              }}
            >
              {isTogglingArchive ? 'Processing' : isArchived ? 'Unarchive' : 'Archive Request'}
            </Button>
          )}
        </Stack>

        <Box>
          <Typography fontSize={'0.75rem'} lineHeight={'1rem'} fontWeight={700} color={'#6C6C6C'}>
            Note:
          </Typography>
          <Box
            component={'ul'}
            sx={{
              p: 0,
              ml: '1rem',
            }}
          >
            <Typography
              fontSize={'0.75rem'}
              lineHeight={'1rem'}
              fontWeight={700}
              color={'#6C6C6C'}
              component={'li'}
            >
              Archive will remove the request from active tracking, you will have the option to
              'Unarchive' the request.
            </Typography>
            <Typography
              fontSize={'0.75rem'}
              lineHeight={'1rem'}
              fontWeight={700}
              color={'#6C6C6C'}
              component={'li'}
            >
              Delete will remove this self serve request from the platform.
            </Typography>
          </Box>
        </Box>
      </Stack>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        maxWidth={false}
        sx={{
          zIndex: 9999,
          '& .MuiDialog-paper': {
            width: '420px',
            borderRadius: '28px',
            padding: '24px',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '24px',
            lineHeight: '32px',
            fontWeight: 700,
            color: '#162936',
            fontFamily: 'Manrope, sans-serif',
            p: 0,
          }}
        >
          Delete Self Serve Request?
        </DialogTitle>
        <DialogContent sx={{ p: 0, mt: 2 }}>
          <Typography
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 500,
              color: '#6C6C6C',
            }}
          >
            Are you sure you want to delete this self serve request? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 0, mt: 3 }}>
          <Button
            disableElevation
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={handleConfirmDelete}
            variant="outlined"
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              padding: '10px 24px',
              border: 'none',
              borderRadius: '100px',
              color: '#51B8B0',
              '&:hover': {
                border: 'none',
                background: '#fff',
              },
              textTransform: 'none',
            }}
          >
            Delete
          </Button>
          <Button
            disableElevation
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={() => setIsDeleteDialogOpen(false)}
            variant="contained"
            sx={{
              background: theme.palette.primary.main,
              borderRadius: '100px',
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              padding: '10px 24px',
              color: '#F6F8F9',
              '&:hover': {
                background: theme.palette.primary.main,
              },
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeleteArchiveSection;
